import { useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { LoadingState, ErrorState, EmptyState, DemoDataNotice } from "@/components/ui/AsyncState";
import { CallOutcomeBadge } from "@/components/ui/StatusBadge";
import { useApi } from "@/lib/use-api";
import { formatPhone, formatDateTime, formatDuration } from "@/lib/format";
import type { CallWithLead, CallOutcome } from "@platform/shared";

const OUTCOME_FILTERS: { value: CallOutcome | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "missed", label: "Missed" },
  { value: "answered", label: "Answered" },
  { value: "voicemail", label: "Voicemail" },
];

export function Calls() {
  const { data, isLoading, error, refetch } = useApi<{ calls: CallWithLead[] }>("/api/calls");
  const [filter, setFilter] = useState<CallOutcome | "all">("all");

  const calls = data?.calls ?? [];
  const filtered = useMemo(
    () => (filter === "all" ? calls : calls.filter((c) => c.outcome === filter)),
    [calls, filter]
  );
  const missedCount = calls.filter((c) => c.outcome === "missed").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Calls</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Every inbound call to your business line.
          {missedCount > 0 && <span className="ml-1 font-medium text-signal-600">{missedCount} missed.</span>}
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {OUTCOME_FILTERS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setFilter(opt.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              filter === opt.value
                ? "border-brand-700 bg-brand-700 text-white"
                : "border-line text-ink-soft hover:text-ink"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {isLoading && <LoadingState label="Loading calls…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState>{calls.length === 0 ? "No calls recorded yet." : "No calls match this filter."}</EmptyState>
      )}

      {filtered.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-paper text-ink-soft">
              <tr>
                <th className="px-5 py-2.5 font-medium">From</th>
                <th className="px-5 py-2.5 font-medium">Lead</th>
                <th className="px-5 py-2.5 font-medium">Outcome</th>
                <th className="px-5 py-2.5 font-medium">Duration</th>
                <th className="px-5 py-2.5 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((call) => (
                <tr
                  key={call.id}
                  className={`border-b border-line last:border-0 ${
                    call.outcome === "missed" ? "bg-signal-50/60" : ""
                  }`}
                >
                  <td className="px-5 py-3 font-medium text-ink">{formatPhone(call.fromNumber)}</td>
                  <td className="px-5 py-3 text-ink-soft">{call.leadName ?? "—"}</td>
                  <td className="px-5 py-3">
                    <CallOutcomeBadge outcome={call.outcome} />
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{formatDuration(call.durationSeconds)}</td>
                  <td className="px-5 py-3 text-ink-soft">{formatDateTime(call.occurredAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <DemoDataNotice>
        Calls are recorded in the database, but live detection via a telephony provider isn't connected yet — the
        webhook that would create these rows automatically (`POST /api/webhooks/telephony`) is still a future step.
      </DemoDataNotice>
    </div>
  );
}
